/*!
 * OGGON Recent Purchase — premium social-proof notification controller
 * ---------------------------------------------------------------------
 * Single global controller, mounted once from layout/theme.liquid. Reads its
 * product + city data from a JSON <script> tag rendered by
 * snippets/oggon-purchase-popup.liquid, then owns one popup element for the
 * life of the page.
 *
 * Design notes:
 * - Timing state lives in sessionStorage so the schedule survives normal
 *   storefront navigation but starts fresh in a new tab/browsing session.
 * - The countdown truly pauses (not just "doesn't fire") while the tab is
 *   hidden: the target fire time is pushed forward by exactly how long the
 *   tab was hidden.
 * - The popup will not appear while the cart drawer or the wishlist
 *   drawer/toast is open — it waits and retries a few seconds later instead
 *   of skipping its turn outright.
 * - This is NOT a Shopify section, so it is not subject to section
 *   re-rendering in the theme editor; a simple boot guard still protects
 *   against any accidental double-include of the script tag.
 */
(function () {
  'use strict';

  if (window.__oggonRPInit) {
    return;
  }
  window.__oggonRPInit = true;

  var STORAGE_KEY = 'oggonRecentPurchase.v2';
  var MAX_DISMISSALS = 3;
  var DISPLAY_MS_MIN = 5500;
  var DISPLAY_MS_MAX = 6800;
  var FIRST_MIN = 8000;
  var FIRST_MAX = 15000;
  var SECOND_MIN = 45000;
  var SECOND_MAX = 70000;
  var LATER_MIN = 105000;
  var LATER_MAX = 135000;
  var RETRY_MS = 4000;
  var MAX_RETRIES = 8;

  function rand(min, max) {
    return Math.floor(min + Math.random() * (max - min));
  }

  /* ---------------- storage (falls back to memory if unavailable) ---------------- */

  var memoryState = null;

  function readState() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        return JSON.parse(raw);
      }
    } catch (e) {
      /* private mode / storage disabled — fall through to memory */
    }
    return memoryState;
  }

  function writeState(state) {
    memoryState = state;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* ignore — memoryState still holds it for this page view */
    }
  }

  function freshState() {
    return {
      start: Date.now(),
      next: Date.now() + rand(FIRST_MIN, FIRST_MAX),
      count: 0,
      dismiss: 0,
      lastP: -1,
      lastC: -1,
      lastT: -1,
      stopped: false,
      hiddenAt: null,
    };
  }

  /* ---------------- data ---------------- */

  function loadData() {
    var el = document.getElementById('oggon-rp-data');
    if (!el) {
      return null;
    }
    try {
      var data = JSON.parse(el.textContent || el.innerText || '{}');
      if (!data || !data.products || !data.products.length || !data.cities || !data.cities.length) {
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function pickIndex(poolSize, lastIndex) {
    if (poolSize <= 1) {
      return 0;
    }
    var idx = lastIndex;
    var tries = 0;
    while (idx === lastIndex && tries < 6) {
      idx = rand(0, poolSize);
      tries += 1;
    }
    return idx;
  }

  var LABELS = [
    { text: 'Recently purchased', weight: 75 },
    { text: 'Popular with customers in', weight: 9 },
    { text: 'A popular OGGON choice', weight: 8 },
    { text: 'Chosen for modern workspaces', weight: 8 },
  ];

  function pickLabel() {
    var total = 0;
    var i;
    for (i = 0; i < LABELS.length; i += 1) {
      total += LABELS[i].weight;
    }
    var roll = Math.random() * total;
    var acc = 0;
    for (i = 0; i < LABELS.length; i += 1) {
      acc += LABELS[i].weight;
      if (roll <= acc) {
        return LABELS[i].text;
      }
    }
    return LABELS[0].text;
  }

  /* ---------------- collision-safe vertical offset ---------------- */

  function computeBottomOffset(isMobile) {
    var base = isMobile ? 14 : 24;
    var gap = 12;
    var bottom = base;

    try {
      var satc = document.querySelector('.oggon-satc.is-on');
      if (satc) {
        var r1 = satc.getBoundingClientRect();
        if (r1.height > 0) {
          bottom = Math.max(bottom, window.innerHeight - r1.top + gap);
        }
      }

      // Phone bottom navigation bar (snippets/oggon-mobile-nav.liquid).
      var mnav = document.querySelector('.oggon-mnav:not(.is-away)');
      if (mnav) {
        var r0 = mnav.getBoundingClientRect();
        if (r0.height > 0 && r0.top < window.innerHeight) {
          bottom = Math.max(bottom, window.innerHeight - r0.top + gap);
        }
      }

      var wlToast = document.querySelector('.oggon-wl-toast.is-open');
      if (wlToast) {
        var r2 = wlToast.getBoundingClientRect();
        if (r2.height > 0) {
          bottom = Math.max(bottom, window.innerHeight - r2.top + gap);
        }
      }
    } catch (e) {
      /* keep base offset */
    }

    return bottom;
  }

  function isBlockedByOtherUI() {
    try {
      if (document.body.classList.contains('overflow-hidden')) {
        return true; // Dawn cart drawer / menu drawer open
      }
      if (document.body.classList.contains('oggon-wl-locked')) {
        return true; // wishlist drawer open
      }
      var wlOverlay = document.querySelector('.oggon-wl-overlay.is-open');
      if (wlOverlay) {
        return true;
      }
    } catch (e) {
      /* not blocked */
    }
    return false;
  }

  /* ---------------- controller ---------------- */

  function Controller(data) {
    this.data = data;
    this.state = readState() || freshState();
    if (!readState()) {
      writeState(this.state);
    }
    this.el = null;
    this.fireTimer = null;
    this.hideTimer = null;
    this.repositionTimer = null;
    this.retryCount = 0;
    this.visible = false;

    this.buildDom();
    this.bindLifecycle();
    this.scheduleNext(true);
  }

  Controller.prototype.buildDom = function () {
    var wrap = document.createElement('div');
    wrap.className = 'oggon-rp';
    wrap.setAttribute('role', 'status');
    wrap.setAttribute('aria-live', 'polite');
    // The wishlist script auto-attaches a heart button to any
    // a[href*="/products/"] it finds on the page (see scanCards() in
    // oggon-wishlist.js). This is its own documented escape hatch for
    // opting a component out, so our notification thumbnail doesn't grow
    // an unrelated wishlist heart.
    wrap.setAttribute('data-oggon-wl-skip', '');
    wrap.hidden = true;
    wrap.innerHTML =
      '<div class="oggon-rp__main">' +
      '<a class="oggon-rp__media" href="#" tabindex="-1" aria-hidden="true">' +
      '<img alt="" loading="lazy" width="64" height="64">' +
      '</a>' +
      '<div class="oggon-rp__body">' +
      '<p class="oggon-rp__label">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3z"/><path d="M9 12l2 2 4-4"/></svg>' +
      '<span class="oggon-rp__label-text"></span></p>' +
      '<a class="oggon-rp__title" href="#"></a>' +
      '<p class="oggon-rp__meta">' +
      '<span class="oggon-rp__price"></span>' +
      '<span class="oggon-rp__compare" hidden></span>' +
      '<span class="oggon-rp__rating" hidden><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9z"/></svg><span></span></span>' +
      '</p>' +
      '<p class="oggon-rp__loc">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-6-5.3-6-11a6 6 0 0 1 12 0c0 5.7-6 11-6 11z"/><circle cx="12" cy="10" r="2.2"/></svg>' +
      '<span class="oggon-rp__loc-text"></span></p>' +
      '</div>' +
      '</div>' +
      '<div class="oggon-rp__foot">' +
      '<p class="oggon-rp__trust"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg><span class="oggon-rp__trust-text"></span></p>' +
      '<a class="oggon-rp__cta" href="#">View chair<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></a>' +
      '</div>' +
      '<div class="oggon-rp__bar" aria-hidden="true"><i></i></div>' +
      '<button type="button" class="oggon-rp__close" aria-label="Dismiss notification">' +
      '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M1 1l10 10M11 1L1 11"/></svg>' +
      '</button>';

    document.body.appendChild(wrap);
    this.el = wrap;

    var self = this;
    wrap.querySelector('.oggon-rp__close').addEventListener('click', function () {
      self.dismiss();
    });

    // Let the customer read: hovering (or focusing) the card pauses the
    // auto-hide and the progress line; leaving resumes with a short grace.
    wrap.addEventListener('mouseenter', function () { self.pauseHide(); });
    wrap.addEventListener('mouseleave', function () { self.resumeHide(); });
    wrap.addEventListener('focusin', function () { self.pauseHide(); });
    wrap.addEventListener('focusout', function () { self.resumeHide(); });
  };

  Controller.prototype.bindLifecycle = function () {
    var self = this;

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        self.onHide();
      } else {
        self.onShowTab();
      }
    });

    var repositionIfVisible = function () {
      if (self.visible) {
        self.applyPosition();
      }
    };
    window.addEventListener('resize', repositionIfVisible, { passive: true });
    window.addEventListener('scroll', repositionIfVisible, { passive: true });
  };

  Controller.prototype.onHide = function () {
    if (this.fireTimer) {
      clearTimeout(this.fireTimer);
      this.fireTimer = null;
    }
    this.state.hiddenAt = Date.now();
    writeState(this.state);

    // Don't leave a notification mid-read while the customer is away.
    if (this.visible) {
      this.hidePopup(true);
    }
  };

  Controller.prototype.onShowTab = function () {
    if (this.state.hiddenAt) {
      var elapsed = Date.now() - this.state.hiddenAt;
      if (elapsed > 0) {
        this.state.next += elapsed;
      }
      this.state.hiddenAt = null;
      writeState(this.state);
    }
    this.scheduleNext(false);
  };

  Controller.prototype.scheduleNext = function (isBoot) {
    if (this.state.stopped) {
      return;
    }
    if (document.hidden) {
      return; // will resume via onShowTab
    }
    if (this.fireTimer) {
      clearTimeout(this.fireTimer);
    }
    var remaining = this.state.next - Date.now();
    if (remaining < 0) {
      remaining = isBoot ? rand(300, 900) : 0;
    }
    var self = this;
    this.fireTimer = setTimeout(function () {
      self.tryFire(0);
    }, remaining);
  };

  Controller.prototype.tryFire = function (retries) {
    if (this.state.stopped || document.hidden) {
      return;
    }
    if (isBlockedByOtherUI()) {
      if (retries >= MAX_RETRIES) {
        this.armFollowingSlot();
        return;
      }
      var self = this;
      this.fireTimer = setTimeout(function () {
        self.tryFire(retries + 1);
      }, RETRY_MS);
      return;
    }
    this.fire();
  };

  /* Relative, honest timing for a real order timestamp. */
  function timeAgo(iso) {
    var t = Date.parse(iso || '');
    if (isNaN(t)) { return ''; }
    var d = Math.max(0, Date.now() - t);
    var h = Math.floor(d / 3600000);
    if (h < 1) { return 'just now'; }
    if (h < 24) { return h + (h === 1 ? ' hour ago' : ' hours ago'); }
    var days = Math.floor(h / 24);
    if (days === 1) { return 'yesterday'; }
    if (days < 7) { return days + ' days ago'; }
    var w = Math.floor(days / 7);
    if (w < 5) { return w + (w === 1 ? ' week ago' : ' weeks ago'); }
    var m = Math.floor(days / 30);
    return m + (m === 1 ? ' month ago' : ' months ago');
  }

  Controller.prototype.fire = function () {
    var products = this.data.products;
    var cities = this.data.cities;
    var events = this.data.events || [];
    var trust = this.data.trust || [];
    var tIdx = trust.length ? pickIndex(trust.length, this.state.lastT == null ? -1 : this.state.lastT) : -1;
    this.state.lastT = tIdx;
    this.state.count += 1;

    this.armFollowingSlot();

    if (events.length >= 3) {
      /* Real orders available: show a genuine product + city pair with its real timing. */
      var eIdx = pickIndex(events.length, this.state.lastP);
      this.state.lastP = eIdx;
      var ev = events[eIdx];
      this.render(ev, ev.city, tIdx >= 0 ? trust[tIdx] : '', 'Recently purchased', timeAgo(ev.at));
    } else {
      var pIdx = pickIndex(products.length, this.state.lastP);
      var cIdx = pickIndex(cities.length, this.state.lastC);
      this.state.lastP = pIdx;
      this.state.lastC = cIdx;
      this.render(products[pIdx], cities[cIdx], tIdx >= 0 ? trust[tIdx] : '', '', '');
    }
    this.showPopup();
  };

  Controller.prototype.armFollowingSlot = function () {
    var gap;
    if (this.state.count <= 1) {
      gap = rand(SECOND_MIN, SECOND_MAX);
    } else {
      gap = rand(LATER_MIN, LATER_MAX);
    }
    this.state.next = Date.now() + gap;
    writeState(this.state);
    this.scheduleNext(false);
  };

  Controller.prototype.render = function (product, city, trustFact, fixedLabel, when) {
    var el = this.el;
    var img = el.querySelector('.oggon-rp__media img');
    var link = el.querySelector('.oggon-rp__media');
    var title = el.querySelector('.oggon-rp__title');
    var cta = el.querySelector('.oggon-rp__cta');
    var loc = el.querySelector('.oggon-rp__loc-text');
    var labelText = el.querySelector('.oggon-rp__label-text');
    var price = el.querySelector('.oggon-rp__price');
    var compare = el.querySelector('.oggon-rp__compare');
    var rating = el.querySelector('.oggon-rp__rating');
    var trustText = el.querySelector('.oggon-rp__trust-text');

    img.src = product.image || '';
    img.alt = product.title || '';
    link.href = product.url || '#';
    title.href = product.url || '#';
    cta.href = product.url || '#';
    title.textContent = product.title || '';
    loc.textContent = city || '';
    labelText.textContent = fixedLabel || pickLabel();
    if (when) { labelText.textContent += ' · ' + when; }

    price.textContent = product.price || '';
    if (product.compare) {
      compare.textContent = product.compare;
      compare.hidden = false;
    } else {
      compare.hidden = true;
    }

    var r = parseFloat(product.rating);
    if (r && r >= 4 && product.ratingCount > 0) {
      rating.querySelector('span').textContent = r.toFixed(1);
      rating.hidden = false;
    } else {
      rating.hidden = true;
    }

    trustText.textContent = trustFact || '';
    el.querySelector('.oggon-rp__foot').style.display = trustFact ? '' : 'none';
  };

  Controller.prototype.applyPosition = function () {
    var isMobile = window.innerWidth <= 749;
    var bottom = computeBottomOffset(isMobile);
    this.el.style.setProperty('--oggon-rp-bottom', bottom + 'px');
  };

  Controller.prototype.showPopup = function () {
    var self = this;
    this.el.hidden = false;
    this.applyPosition();

    // force reflow so the enter transition runs
    // eslint-disable-next-line no-unused-expressions
    this.el.offsetHeight;

    this.visible = true;

    requestAnimationFrame(function () {
      // Guard against a rAF callback landing after the popup was already
      // hidden again (can happen under heavy timer/paint throttling).
      if (self.visible) {
        self.el.classList.add('is-visible');
      }
    });

    if (this.repositionTimer) {
      clearInterval(this.repositionTimer);
    }
    this.repositionTimer = setInterval(function () {
      self.applyPosition();
    }, 500);

    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
    }
    var displayMs = rand(DISPLAY_MS_MIN, DISPLAY_MS_MAX);
    this.el.style.setProperty('--oggon-rp-display', displayMs + 'ms');
    this.el.classList.remove('is-paused');
    this.hideAt = Date.now() + displayMs;
    this.hideTimer = setTimeout(function () {
      self.hidePopup(false);
    }, displayMs);
  };

  Controller.prototype.pauseHide = function () {
    if (!this.visible) {
      return;
    }
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.el.classList.add('is-paused');
  };

  Controller.prototype.resumeHide = function () {
    if (!this.visible || this.hideTimer) {
      return;
    }
    var self = this;
    var remaining = Math.max(1800, (this.hideAt || 0) - Date.now());
    this.el.classList.remove('is-paused');
    this.hideTimer = setTimeout(function () {
      self.hidePopup(false);
    }, remaining);
  };

  Controller.prototype.hidePopup = function (immediate) {
    var self = this;
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (this.repositionTimer) {
      clearInterval(this.repositionTimer);
      this.repositionTimer = null;
    }
    if (!this.visible) {
      return;
    }
    this.visible = false;
    this.el.classList.remove('is-visible');
    this.el.classList.remove('is-paused');

    if (immediate) {
      this.el.hidden = true;
      return;
    }

    this.el.classList.add('is-leaving');
    setTimeout(function () {
      self.el.hidden = true;
      self.el.classList.remove('is-leaving');
    }, 360);
  };

  Controller.prototype.dismiss = function () {
    this.hidePopup(false);
    this.state.dismiss += 1;
    if (this.state.dismiss >= MAX_DISMISSALS) {
      this.state.stopped = true;
      if (this.fireTimer) {
        clearTimeout(this.fireTimer);
        this.fireTimer = null;
      }
    }
    writeState(this.state);
  };

  /* ---------------- boot ---------------- */

  function boot() {
    var data = loadData();
    if (!data) {
      return;
    }
    // eslint-disable-next-line no-new
    new Controller(data);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
